import type { ReactNode } from "react";
import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { HydrationBoundary, QueryClientProvider, type DehydratedState, type QueryClient } from "@tanstack/react-query";
import { ExperimentsProvider, type ExperimentVariants } from "../../lib/experiments";
import { ConsoleBanner } from "../ConsoleBanner";
import { KeyboardNav } from "../KeyboardNav";

// The shared shell that wraps every view, on both the server (entry-server.tsx)
// and the client (main.tsx). Anything every page needs around it — providers,
// context, and app-wide chrome — belongs here so the SSR and hydration trees
// stay identical. The active locale is loaded/activated on the shared i18n
// instance before render.
//
// `queryClient` is per-request on the server and the browser singleton on the
// client; `HydrationBoundary` seeds it from the route's dehydrated cache.
export function Shell({
	queryClient,
	dehydratedState,
	experiments = {},
	children,
}: {
	queryClient: QueryClient;
	dehydratedState?: DehydratedState;
	experiments?: ExperimentVariants;
	children: ReactNode;
}) {
	return (
		<I18nProvider i18n={i18n}>
			<QueryClientProvider client={queryClient}>
				<HydrationBoundary state={dehydratedState}>
					<ExperimentsProvider variants={experiments}>{children}</ExperimentsProvider>
				</HydrationBoundary>
				<ConsoleBanner />
				<KeyboardNav />
			</QueryClientProvider>
		</I18nProvider>
	);
}
